/**
 * @file AddPageDialog.tsx
 * @description «Новая страница» (phase19.16 Р1-6а; uiux-builder-shell.md
 * §2.2): название, адрес (подставляется из названия латиницей, можно
 * поправить), «Сразу скрыть — опубликую, когда соберу» (Р3а: иначе —
 * первая версия с показом). После создания
 * открывается пустая страница: шапка, «На странице пока нет блоков», подвал.
 * Окно 560px, на телефоне — на весь экран.
 */

import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useNotify } from "../../ui/NotifyProvider";
import { CheckRow } from "../../site-view/CheckRow";
import { TextField } from "../../site-view/TextField";
import { useSiteView } from "../../site-view/site-view-context";
import { useBuilder } from "../builder-context";
import { ApiError } from "../../../lib/api-client";
import { sitePagePublish } from "../../../lib/api-site-page-publish";
import { pagePublishErrorText } from "../../../lib/site-builder/page-publish-errors";
import { cleanSlugInput, PAGE_ERROR_FIELDS, PAGE_TITLE_MAX, pageFormErrors, slugFromTitle, type PageFormErrors } from "../../../lib/site-builder/page-form";
import { siteHost } from "../../../lib/site-view/seo";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AddPageDialog({ open, onClose }: Props) {
  const b = useBuilder();
  const view = useSiteView();
  const notify = useNotify();
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [hidden, setHidden] = useState(true);
  const [errors, setErrors] = useState<PageFormErrors>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setSlug("");
    setSlugTouched(false);
    setHidden(true);
    setErrors({});
  }, [open]);

  async function onCreate() {
    const found = pageFormErrors({ title, slug, seoTitle: "", seoDescription: "", noIndex: false });
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setBusy(true);
    try {
      const page = await b.pages.create({ title: title.trim(), slug });
      // Новая страница создаётся скрытой; показать сразу — по выбору и при праве:
      // Р3а — первая версия (пустая страница) с `show: true` по отпечатку черновика.
      let shown = false;
      if (!hidden && b.canPublishPages) {
        try {
          const draft = await sitePagePublish.changes(page.id);
          await sitePagePublish.publish(page.id, { fingerprint: draft.fingerprint, show: true });
          shown = true;
        } catch (err) {
          // Страница уже есть — не держим окно: открываем её скрытой и объясняем.
          notify.error(`Страница создана скрытой — показать не удалось. ${pagePublishErrorText(err, "Покажите её позже кнопкой «Показать на сайте».")}`);
        }
      }
      await b.pages.reloadList();
      onClose();
      if (hidden) notify.success("Страница создана — она скрыта, пока вы её не покажете");
      else if (shown) notify.success("Страница создана и показана на сайте");
      await b.select({ pageId: page.id });
    } catch (err) {
      const mapped = err instanceof ApiError ? PAGE_ERROR_FIELDS[err.code] : undefined;
      if (mapped) setErrors({ [mapped.field]: mapped.text });
      else notify.error(err instanceof ApiError && err.message ? err.message : "Не удалось создать страницу.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-[35rem]">
        <DialogHeader>
          <DialogTitle>Новая страница</DialogTitle>
          <DialogDescription>Соберёте её из блоков в конструкторе. Покупатели увидят страницу, когда вы её покажете.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <TextField
            label="Название"
            value={title}
            max={PAGE_TITLE_MAX}
            placeholder="О мастерской"
            error={errors.title}
            onChange={(next) => {
              setTitle(next);
              if (!slugTouched) setSlug(slugFromTitle(next));
            }}
          />
          <TextField
            label={`Адрес: ${siteHost(view.siteUrl)}/…`}
            value={slug}
            placeholder="about"
            hint="Латиница, цифры и дефис. Подставляется из названия."
            error={errors.slug}
            onChange={(next) => {
              setSlugTouched(true);
              setSlug(cleanSlugInput(next));
            }}
          />
          {b.canPublishPages ? (
            <CheckRow checked={hidden} onChange={setHidden}>
              Сразу скрыть — опубликую, когда соберу
            </CheckRow>
          ) : (
            <p className="type-caption text-on-surface-variant">Страница будет скрыта: показать её на сайте может сотрудник с правом публикации контента.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button loading={busy} onClick={() => void onCreate()}>
            Создать страницу
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
